import { useFundingStages } from 'apis/CompanyAPI/labels/useFundingStages';
import { useIndustries } from 'apis/CompanyAPI/labels/useIndustries';
import { useStages } from 'apis/CompanyAPI/labels/useStages';
import { OnboardingPart, OnboardingStep } from 'apis/OnboardingAPI';
import BasicCompanyProfileForm from 'domain/onboarding/company/Wizard/BasicCompanyProfileForm';
import IndustryForm from 'domain/onboarding/company/Wizard/IndustryForm';
import KpisForm from 'domain/onboarding/company/CompanyKpis/KpisForm';
import StagesForm from 'domain/onboarding/company/Wizard/StagesForm';
import React, { useEffect, useState } from 'react';
import { CompanyProfile, ICompany } from 'types/company';
import Wizard, { OnboardingStage } from 'ui/modules/wizards/Wizard';
import { DialogActions } from 'ui/views/dialogs/Dialog';
import { track } from 'util/analytics';
import { getOrElse } from 'util/resource';
import ButtonList from 'ui/elements/buttons/ButtonList';
import Button from 'ui/elements/buttons/Button';
import { useCompanyProfile } from 'apis/CompanyAPI/companies/useCompanyProfile';
import Resource from 'util/resource/Resource';
import { companiesApi } from 'apis/CompanyAPI/companies/companiesApi';
import PostUpdateForm from './PostUpdateForm';
import { useCommunities } from 'apis/CompanyAPI/communities/useCommunities';
import { userDashboardKey } from 'pages/Dashboard/useUserDashboardData';
import { invalidate } from 'hooks/useSWR';
import AiOnboarding, { useAiOnboardingSessionStorage } from './AIOnboarding';
import InviteConnectionForm from 'domain/shared/InviteConnectionForm';
import { getPublicCompanyUrl } from 'pages/CompanySettings/CompanySharing';
import { useCompanyLastUpdated } from 'domain/companies/profile/LastUpdated';

export default function CompanyOnboardingWizard({
  company,
  wizard,
  setStepCompleted,
  onClose,
}: {
  wizard: OnboardingPart;
  company: ICompany;
  setStepCompleted: (step: OnboardingStep | OnboardingStep[]) => void;
  onClose: (status: 'Skipped' | 'Completed') => void;
}) {
  const { resource, mutate } = useCompanyProfile(company.slug);

  return (
    <Resource resource={resource} renderLoading="Nothing">
      {companyProfile => (
        <CompanyWizard
          setCompanyProfile={mutate}
          companyProfile={companyProfile}
          onboarding={wizard}
          setStepCompleted={setStepCompleted}
          onClose={onClose}
        />
      )}
    </Resource>
  );
}

export type OnboardingMode = 'manual input' | 'ai' | 'ai failed';

function CompanyWizard({
  companyProfile,
  setCompanyProfile,
  onboarding,
  setStepCompleted,
  onClose,
}: {
  companyProfile: CompanyProfile;
  setCompanyProfile: (companyProfile: CompanyProfile) => void;
  onboarding: OnboardingPart;
  setStepCompleted: (step: OnboardingStep | OnboardingStep[]) => void;
  onClose: (status: 'Skipped' | 'Completed') => void;
}) {
  const { resource: industriesResource } = useIndustries();
  const { resource: fundingStagesResource } = useFundingStages();
  const { resource: stagesResource } = useStages();
  const { resource: communitesResource } = useCommunities();

  const industries = getOrElse(industriesResource, { parents: [], children: [] });
  const productStages = getOrElse(stagesResource, { values: [], industryDependent: [] });
  const fundingStages = getOrElse(fundingStagesResource, { values: [] });

  const { setLastUpdated } = useCompanyLastUpdated(companyProfile.id);

  useEffect(() => {
    track('company-onboarding-started');
  }, [onboarding]);

  const completedStages = onboarding.steps.filter(step => step.isCompleted).map(step => step.step);
  const [isAiOnboarding] = useAiOnboardingSessionStorage();

  type Steps =
    | 'Welcome'
    | 'IndustryAndBusinessFocus'
    | 'ProductAndFundingStage'
    | 'Kpis'
    | 'BasicProfile'
    | 'InviteYourStakeholders'
    | 'PostFirstUpdate';

  function onCloseWizard(status: 'Skipped' | 'Completed', reload = true) {
    onClose(status);
    if (reload) {
      invalidate(userDashboardKey);
    }
    track('company-onboarding-completed');
  }

  // industry step is the first, so if they filled that out, we can assume they don't want to use AI onboarding
  const hasStartedOnboarding = companyProfile.industries.values.length > 0;
  const [onboardingMode, setOnboardingMode] = useState<OnboardingMode>(hasStartedOnboarding ? 'manual input' : 'ai');

  const stages: OnboardingStage<Steps>[] = [
    {
      name: 'IndustryAndBusinessFocus',
      title: 'What is your industry and business focus?',
      content: ({ onComplete }) => (
        <IndustryForm
          industries={industries.parents}
          companyProfile={companyProfile}
          setOnboardingMode={setOnboardingMode}
          onboardingMode={onboardingMode}
          onComplete={selectedIndustries => {
            setCompanyProfile({ ...companyProfile, industries: { values: selectedIndustries } });
            setStepCompleted('IndustryAndBusinessFocus');
            onComplete();
          }}
          onClose={() => onCloseWizard('Skipped')}
        />
      ),
    },
    {
      name: 'ProductAndFundingStage',
      title: 'What is your current product and funding stage?',
      content: ({ onComplete, onBack }) => (
        <StagesForm
          stages={productStages}
          fundingStages={fundingStages.values}
          companyProfile={companyProfile}
          onComplete={(selectedStage, selectedFundingStage) => {
            setCompanyProfile({
              ...companyProfile,
              stages: { values: selectedStage ? [selectedStage] : [] },
              fundingStage: selectedFundingStage,
            });
            setStepCompleted('ProductAndFundingStage');
            onComplete();
          }}
          onBack={onBack}
          onClose={() => onCloseWizard('Skipped')}
        />
      ),
    },
    {
      name: 'Kpis',
      title: 'What are your relevant KPIs for traction?',
      content: ({ onComplete, onBack }) => (
        <>
          <KpisForm
            companyId={companyProfile.id}
            onKpiUploaded={() => {
              setStepCompleted('Kpis'); // we don't reload onboarding here because this status is calculated by async events backend, and might not be updated by the time the user gets the new status
            }}
          />
          <DialogActions align="space-between">
            <ButtonList>
              <Button kind="primary" type="submit" onClick={onComplete}>
                Next
              </Button>
              <Button onClick={onBack} kind="primary" color="greyLight">
                Back
              </Button>
            </ButtonList>
            <Button onClick={() => onCloseWizard('Skipped', false)} kind="tertiary" color="grey">
              Pick it up later
            </Button>
          </DialogActions>
        </>
      ),
    },
    {
      name: 'BasicProfile',
      title: isAiOnboarding ? 'Review your profile' : 'Build your profile',
      content: ({ onComplete, onBack }) => (
        <BasicCompanyProfileForm
          companyProfile={companyProfile}
          setCompanyProfile={setCompanyProfile}
          onComplete={onComplete}
          onClose={() => onCloseWizard('Skipped')}
          onBack={onBack}
        />
      ),
    },
    {
      name: 'InviteYourStakeholders',
      title: 'Share and connect',
      content: ({ onComplete, onBack }) => (
        <InviteConnectionForm
          onComplete={() => {
            setLastUpdated(new Date());
            onComplete();
          }}
          onClose={() => onCloseWizard('Skipped')}
          onBack={onBack}
          inviteFunction={emails =>
            companiesApi.userInvites.invite(
              companyProfile.id,
              emails.map(email => ({ role: 'prospective_investor', email })),
            )
          }
          inviteLabelText={'Invite company connections by email'}
          shareLabelText={'Or invite connections by link'}
          footerText={`Connections get access to your company profile and future updates. You can easily change permissions to
          investor and board member access for confidential information at a later stage. If your connections are
          already on the platform they will be notified directly. Others will recieve an invitation to join your
          company.`}
          publicUrl={getPublicCompanyUrl(companyProfile.slug)}
          isLastStep={false}
        />
      ),
    },
    {
      name: 'PostFirstUpdate',
      title: 'Create your first update for future connections',
      content: ({ onComplete, onBack }) => (
        <PostUpdateForm
          communities={getOrElse(communitesResource, { values: [] }).values}
          companyProfile={companyProfile}
          onBack={onBack}
          onClose={() => onCloseWizard('Skipped')}
          onComplete={() => {
            onComplete();
            invalidate(userDashboardKey);
            onCloseWizard('Completed');
          }}
        />
      ),
    },
  ];

  if (onboardingMode === 'ai') {
    return (
      <AiOnboarding
        companyProfile={companyProfile}
        onComplete={() => {
          setStepCompleted(['IndustryAndBusinessFocus', 'ProductAndFundingStage', 'Kpis']);
          setOnboardingMode('manual input');
        }}
        setOnboardingMode={setOnboardingMode}
      />
    );
  }

  return <Wizard completedStages={completedStages} stages={stages} />;
}
